import React from "react";
import { createRoot } from "react-dom/client";

import { App } from "./app.jsx";
import { clearLinkSession, initAuth, isConfigured, isLinkSession, login, logout } from "./auth.js";
import { NotAuthenticated, applyBootstrap, loadBootstrap, requestVerificationEmail, setSelectedRealm } from "./boot.js";
import { captureInvitation } from "./invitation_session.js";
import { InvitationRedeem } from "./invitation_redeem.jsx";

// Entry point for the static bundle.
//
// The bundle is served from the static origin and knows nothing until the host
// answers. Until the payload lands there is no data layer, so App cannot mount:
// every view reads window.DATA synchronously. The three states below are
// therefore ordered — loading, then either the app or an explicit failure — and
// a failed load never falls through to a rendered dashboard.

function Loading() {
  return (
    <div className="boot-state" role="status">
      Loading your dashboard…
    </div>
  );
}

/** A bootstrap failure is reported as a failure, never as an empty dashboard. */
function BootError({ detail }) {
  return (
    <div className="boot-state boot-state--error" role="alert">
      <h1>The dashboard could not be loaded</h1>
      <p>
        Your lots and statistics could not be reached just now. Nothing has been lost — reload the
        page to try again.
      </p>
      <p className="boot-state__detail">{detail}</p>
    </div>
  );
}

/** Shown when there is no session. Signing in is the visitor's action, not ours. */
function SignIn() {
  return (
    <div className="boot-state">
      <h1>Inglis Vendor Dashboard</h1>
      <p>Sign in to see your lots, their statistics and today&rsquo;s sale information.</p>
      <button type="button" className="btn primary" onClick={() => login()}>
        Sign in
      </button>
    </div>
  );
}

/**
 * The "send me a new verification link" action, shared by the two states that
 * offer it. Kept in one place so the message a visitor sees for a rate limit or
 * an unavailable sender cannot drift between screens.
 */
function useVerificationSend() {
  const [state, setState] = React.useState({ busy: false, message: "", sent: false });

  const send = async () => {
    setState({ busy: true, message: "", sent: false });
    try {
      const result = await requestVerificationEmail();
      if (result.alreadyVerified) {
        setState({ busy: false, sent: false, message: "Your email is already verified — choose Check again." });
      } else if (result.rateLimited) {
        setState({ busy: false, sent: false, message: "A link was sent recently. Please wait a minute and try again." });
      } else if (result.unavailable) {
        setState({ busy: false, sent: false, message: "We cannot send the link from here just now. Please contact your Inglis representative." });
      } else {
        setState({ busy: false, sent: true, message: "Sent. Check your inbox, and your spam folder." });
      }
    } catch (error) {
      setState({ busy: false, sent: false, message: "We could not send a new link just now. Please try again." });
    }
  };

  return { state, send };
}

/**
 * Shown when the sign-in succeeded but this application has no access on file.
 *
 * This is the designed outcome of a first sign-in, not a fault: the binding
 * between an Auth0 account and a vendor is created by a reviewed provisioning
 * step, and never by the act of signing in. The copy says so plainly rather
 * than implying something is broken, and it names nothing about who does have
 * access.
 */
function NoAccess({ canResend = false }) {
  const { state, send } = useVerificationSend();

  return (
    <div className="boot-state">
      <h1>No dashboard access yet</h1>
      <p>
        You are signed in, but this account has not been given access to a vendor&rsquo;s lots.
        Inglis needs to grant it before the dashboard can show you anything.
      </p>
      <p>If you were expecting access, contact your Inglis representative.</p>
      {/*
        Offered without asserting anything about this visitor's address. Access
        may already have been arranged and be waiting on a confirmation only
        they can complete, and when the provider did not tell us whether the
        address is confirmed, this is the one step they can take themselves.
        Saying "if you have not" keeps it a suggestion rather than a diagnosis,
        and it discloses nothing about whether a membership exists.
      */}
      {canResend ? (
        <p>
          If you have not confirmed your email address yet, you can request a new link — then
          choose Check again.
        </p>
      ) : null}
      {canResend && state.message ? <p className="boot-state__detail">{state.message}</p> : null}
      <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
        {canResend ? (
          <>
            <button type="button" className="btn" onClick={send} disabled={state.busy || state.sent}>
              {state.busy ? "Sending…" : "Send a new link"}
            </button>
            <button type="button" className="btn primary" onClick={() => window.location.reload()}>
              Check again
            </button>
          </>
        ) : null}
        <button type="button" className="btn" onClick={() => logout()}>
          Sign out
        </button>
      </div>
    </div>
  );
}

/**
 * Shown when the visitor holds a session the host will not accept.
 *
 * Offering "sign in" here would be a loop: the identity provider still has a
 * session, returns immediately, and the host refuses again — which is what a
 * rejected token looked like before this state existed. Signing in again cannot
 * fix it, so this says so and offers the two things that can: signing out to
 * try a different account, or telling Inglis.
 */
function SessionRejected() {
  return (
    <div className="boot-state boot-state--error" role="alert">
      <h1>We could not verify your sign-in</h1>
      <p>
        You signed in successfully, but the dashboard could not accept the result. Signing in again
        will not help — this needs fixing at our end.
      </p>
      <p>Please tell your Inglis representative you saw this.</p>
      <button type="button" className="btn" onClick={() => logout()}>
        Sign out
      </button>
    </div>
  );
}

/**
 * Shown when the host refuses the staff access link this page was opened with.
 *
 * The copy is deliberately plain. Revoked, expired and never valid are one
 * state here: saying which would tell whoever holds the URL something about a
 * key they are not entitled to, and none of the three is fixed from this page.
 * There is no sign-in to offer either — a link holder has no account.
 */
function LinkInactive() {
  return (
    <div className="boot-state">
      <h1>This link is no longer active</h1>
      <p>Ask the vendor who gave it to you for a new one.</p>
    </div>
  );
}

function RealmPicker({ access }) {
  const [busy, setBusy] = React.useState(false);
  const choose = (event) => {
    const realm = event.target.value;
    if (!realm) return;
    setBusy(true);
    setSelectedRealm(realm);
    const url = new URL(window.location.href);
    url.searchParams.set("realm", realm);
    window.history.replaceState({}, "", url.toString());
    // Re-bootstrap and remount the shell so no data from the old realm remains
    // visible while requests for the new realm are in flight.
    loadBootstrap()
      .then((next) => {
        applyBootstrap(next);
        if (next.access && next.access.bound) root.render(<App />);
        else root.render(<RealmPicker access={next.access || { realms: [] }} />);
      })
      .catch(() => root.render(<BootError detail="The selected realm could not be loaded." />));
  };
  return (
    <div className="boot-state">
      <h1>Choose a vendor</h1>
      <p>Choose a dashboard you can access to continue.</p>
      <select aria-label="Vendor realm" onChange={choose} disabled={busy} defaultValue="">
        <option value="">Choose a vendor…</option>
        {(access.realms || []).map((realm) => (
          <option key={realm.key} value={realm.key}>{realm.name || realm.key}</option>
        ))}
      </select>
      <button type="button" className="btn" onClick={() => logout()}>Sign out</button>
    </div>
  );
}

/**
 * Shown when the provider has not verified this visitor's email address.
 *
 * Distinct from NoAccess because it is the one "no access" case the visitor can
 * actually resolve themselves. Access was very likely already arranged for
 * them: the provider's verified-email fact is required by the access policy,
 * so without this they would be told to contact Inglis about something only
 * they can do.
 *
 * "Check again" simply reloads. The host reads verification status from the
 * provider on each request rather than from a cached token claim, so a visitor
 * who has just clicked the link is let straight in — no new sign-in needed.
 */
function VerifyEmail() {
  const { state, send } = useVerificationSend();

  return (
    <div className="boot-state">
      <h1>Verify your email address</h1>
      <p>
        Before we can give you access, Inglis needs to confirm your email address is yours. Open
        the link we sent you, then choose Check again.
      </p>
      {state.message ? <p className="boot-state__detail">{state.message}</p> : null}
      <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
        <button type="button" className="btn" onClick={send} disabled={state.busy || state.sent}>
          {state.busy ? "Sending…" : "Send a new link"}
        </button>
        <button type="button" className="btn primary" onClick={() => window.location.reload()}>
          Check again
        </button>
        <button type="button" className="btn" onClick={() => logout()}>
          Sign out
        </button>
      </div>
    </div>
  );
}

const root = createRoot(document.getElementById("root"));
root.render(<Loading />);

/**
 * Start the app.
 *
 * Order matters: authentication is resolved first, because a deployed host
 * requires a verified token on the bootstrap route and answers 401 without one.
 *
 * A build with no Auth0 configuration — the local preview — skips sign-in and
 * requests the payload unauthenticated. That is not a way around the gate: the
 * host is the enforcement point, and it drops the requirement only in `local`
 * and `ci`, which have no tenant to authenticate against and no real data.
 */
async function start() {
  const invitation = captureInvitation();
  const session = await initAuth();
  // boot.js loaded before Auth0 callback handling, so refresh its in-memory
  // realm selector from the sanitized post-callback URL before any fetch.
  setSelectedRealm(new URLSearchParams(window.location.search).get("realm"));

  if (session.configured && !session.authenticated) {
    root.render(<SignIn />);
    return;
  }

  // A captured invitation outlives the tab that opened it for fifteen minutes.
  // A link session reports itself authenticated, so without the link check a
  // staff member opening their link in that tab would be handed someone else's
  // invitation redemption instead of the dashboard their key addresses.
  if (invitation && session.authenticated && !isLinkSession()) {
    root.render(<InvitationRedeem invitation={invitation}/>);
    return;
  }

  let boot;
  try {
    boot = await loadBootstrap();
  } catch (error) {
    // The host rejected the credential rather than failing to answer. What to
    // do about that depends on whether this page believes it has a session:
    // without one, signing in is the fix; with one, signing in is a loop,
    // because the identity provider will hand back the same session the host
    // has just refused.
    if (error instanceof NotAuthenticated) {
      // A link session reports itself authenticated, so it would otherwise
      // land on SessionRejected and tell a staff member to report a fault at
      // our end. A refused link is not a fault: it is the ordinary end of a
      // key's life. The token is dropped first so nothing re-presents it.
      if (isLinkSession()) {
        clearLinkSession();
        root.render(<LinkInactive />);
        return;
      }
      root.render(session.authenticated ? <SessionRejected /> : <SignIn />);
      return;
    }
    throw error;
  }

  // Verified, but with no application binding behind the identity. The host has
  // already withheld every row; this renders that state as itself rather than
  // as a dashboard that looks empty.
  if (boot.access && boot.access.bound === false) {
    // A link the host answers but has no access behind. The three states below
    // are written for an account: RealmPicker offers a choice a link does not
    // have, and both the others offer a sign-in or sign-out a link holder
    // cannot complete. It is the same end of a key's life as a 401, so it
    // reports the same way, and the token is dropped first.
    if (isLinkSession()) {
      clearLinkSession();
      root.render(<LinkInactive />);
      return;
    }
    if (boot.access.identityBound && (boot.access.realms || []).length > 0) {
      root.render(<RealmPicker access={boot.access} />);
      return;
    }
    // An unverified address is the one case the visitor can resolve themselves,
    // so it gets its own state rather than being told to contact Inglis.
    if (boot.access.emailVerified === false) {
      root.render(<VerifyEmail />);
      return;
    }
    root.render(<NoAccess canResend={boot.access.canResendVerification === true} />);
    return;
  }

  applyBootstrap(boot);
  root.render(<App />);
}

start().catch((error) => {
  // Surfaced for operators; the vendor-facing copy stays non-technical.
  console.error("vendor dashboard start-up failed", error);
  root.render(<BootError detail={String(error && error.message ? error.message : error)} />);
});

// Reported once so an operator inspecting a deployed page can tell a bundle
// built without Auth0 from a session that simply expired.
if (!isConfigured()) {
  console.warn("vendor dashboard: this bundle was built without Auth0 configuration");
}
